import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { Shield, Users, Crosshair, CheckCircle, XCircle, Loader } from 'lucide-react'

const A = {
  page: { minHeight: '100vh', background: 'radial-gradient(ellipse at 50% 10%, rgba(77,214,255,.08), transparent 60%), #0e1116', color: '#eaf2fb', fontFamily: "'Saira Condensed', 'Rajdhani', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center' },
  display: { fontFamily: "'Orbitron', 'Rajdhani', sans-serif", letterSpacing: '0.12em', textTransform: 'uppercase' },
  muted: { color: '#9aa6b0' },
  faded: { color: '#5a6470' },
  panel: { background: 'linear-gradient(135deg, #2a2e36, #1a1d22)', border: '1px solid #4a5058', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.06), 0 4px 12px rgba(0,0,0,.4)', padding: '24px' },
  btnPrimary: { width: '100%', fontFamily: "'Orbitron', sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'linear-gradient(135deg, #ff7a18, #d8500a)', color: '#fff5e8', border: 'none', padding: '14px 20px', cursor: 'pointer', boxShadow: '0 0 14px rgba(255,122,24,.5)' },
  btnGhost: { width: '100%', marginTop: '8px', padding: '10px', fontSize: '13px', color: '#5a6470', background: 'none', border: 'none', cursor: 'pointer' },
}

const typeIcon = {
  event: <Crosshair size={24} style={{ color: '#ff7a18' }} />,
  event_squad: <Shield size={24} style={{ color: '#4dd6ff' }} />,
  team: <Users size={24} style={{ color: '#4dd6ff' }} />,
}
const typeLabel = { event: 'Event', event_squad: 'Event + Squad', team: 'Team' }

export default function JoinEvent() {
  // App.jsx registers BOTH `/join/:code` and `/join/:token` — react-router
  // matches them in declaration order, so `/join/:code` always wins and the
  // useParams() value lives on `code`, not `token`. Read both and prefer
  // whichever is set. Bug 51 (re-open): without this the lookup got undefined
  // and both the join_tokens AND events.join_code queries returned empty,
  // surfacing as "Invalid or expired join link."
  const params = useParams()
  const code = params.token || params.code || ''
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [status, setStatus] = useState('loading')
  const [tokenData, setTokenData] = useState(null)
  const [eventData, setEventData] = useState(null)
  const [teamData, setTeamData] = useState(null)
  const [factionData, setFactionData] = useState(null)
  const [message, setMessage] = useState('')

  useEffect(() => { if (!authLoading) validateToken() }, [code, authLoading])

  // ANU-42 — registration gate. Only 'registration' (and 'locked', so a
  // late-add roster patch still works) accept joins. Everything else
  // gets a phase-specific reason so the player knows whether to wait or
  // give up. NULL/unexpected statuses fall through to the generic
  // "registration closed" copy.
  const REGISTRATION_OPEN_STATUSES = new Set(['registration', 'locked'])
  const phaseBlockedMessage = (status) => {
    if (status === 'draft' || status === 'prep' || status === 'pending' || status === 'scheduled') {
      return "Registration hasn't opened yet. Check back when the GM opens this event."
    }
    if (status === 'live') return 'This event is already live — registration is closed.'
    if (status === 'endex') return 'This event has ended — registration is closed.'
    if (status === 'closed') return 'This event is closed — registration is closed.'
    if (status === 'cancelled') return 'This event was cancelled.'
    return 'Registration is closed for this event.'
  }

  const validateToken = async () => {
    setStatus('validating')
    if (!code) {
      setStatus('error'); setMessage('No join code in URL.')
      return
    }

    // Two flavours land on /join/<code> —
    //   1. join_tokens.token  — one-shot UUID from ShareQR / InviteOperator
    //      (buddy / squad / team invite). Carries event_id / team_id / faction_id.
    //   2. events.join_code   — short reusable code printed on the event QR
    //      from GameEvent + QRDownloadCenter. No token row to consume.
    const { data: tok } = await supabase.from('join_tokens').select('*').eq('token', code).limit(1)
    if (tok?.[0]) {
      const t = tok[0]
      if (t.used_at) { setStatus('used'); return }
      if (new Date(t.expires_at) < new Date()) { setStatus('expired'); return }
      setTokenData(t)
      let eventRow = null
      if (t.event_id) { const { data } = await supabase.from('events').select('*, organizations(name)').eq('id', t.event_id).limit(1); eventRow = data?.[0] || null; setEventData(eventRow) }
      if (t.team_id) { const { data } = await supabase.from('teams').select('*').eq('id', t.team_id).limit(1); setTeamData(data?.[0] || null) }
      if (t.faction_id) { const { data } = await supabase.from('event_factions').select('*').eq('id', t.faction_id).limit(1); setFactionData(data?.[0] || null) }
      if (!user) { localStorage.setItem('dc_join_token', code); navigate(`/auth?redirect=/join/${code}`); return }
      // ANU-42 — event-bound tokens are gated by registration phase.
      // Team-only tokens skip this check (team membership has no event
      // lifecycle).
      if (eventRow && !REGISTRATION_OPEN_STATUSES.has(eventRow.status)) {
        setStatus('error'); setMessage(phaseBlockedMessage(eventRow.status))
        return
      }
      setStatus('ready')
      return
    }

    const { data: ev } = await supabase.from('events').select('*, organizations(name)').ilike('join_code', code).limit(1)
    if (ev?.[0]) {
      const e = ev[0]
      // Synthesize a tokenData shape so the existing render branches still
      // work. isEventCode tells executeJoin to skip the join_tokens consume
      // step — there's no row to mark used.
      setTokenData({ join_type: 'event', event_id: e.id, isEventCode: true })
      setEventData(e)
      if (!user) { localStorage.setItem('dc_join_token', code); navigate(`/auth?redirect=/join/${code}`); return }
      // ANU-42 — registration phase gate. Same logic as the
      // join_tokens path above.
      if (!REGISTRATION_OPEN_STATUSES.has(e.status)) {
        setStatus('error'); setMessage(phaseBlockedMessage(e.status))
        return
      }
      setStatus('ready')
      return
    }

    setStatus('error'); setMessage('Invalid or expired join link.')
  }

  const executeJoin = async () => {
    if (!user || !tokenData) return; setStatus('processing')
    try {
      // ANU-42 — re-check phase right before the insert. The status the
      // validation step locked in may have shifted between validate and
      // the user actually tapping JOIN (GM flips event to live, etc.).
      if (tokenData.event_id && (tokenData.join_type === 'event' || tokenData.join_type === 'event_squad')) {
        const { data: phaseCheck } = await supabase.from('events').select('status').eq('id', tokenData.event_id).limit(1)
        const status = phaseCheck?.[0]?.status
        if (!REGISTRATION_OPEN_STATUSES.has(status)) {
          setStatus('error'); setMessage(phaseBlockedMessage(status))
          return
        }
      }
      if (tokenData.join_type === 'event' || tokenData.join_type === 'event_squad') {
        const { data: existing } = await supabase.from('event_players').select('user_id').eq('event_id', tokenData.event_id).eq('user_id', user.id).limit(1)
        if (existing?.[0]) { setStatus('success'); setMessage("You're already registered."); return }
        const { error } = await supabase.from('event_players').insert({ event_id: tokenData.event_id, user_id: user.id, reg_status: 'approved', faction_id: tokenData.join_type === 'event_squad' ? tokenData.faction_id : null, event_role: 'player' })
        if (error) throw error
      } else if (tokenData.join_type === 'team') {
        const { data: existing } = await supabase.from('team_members').select('id').eq('team_id', tokenData.team_id).eq('user_id', user.id).limit(1)
        if (existing?.[0]) { setStatus('success'); setMessage("You're already on this team."); return }
        const { error } = await supabase.from('team_members').insert({ team_id: tokenData.team_id, user_id: user.id, team_role: 'member' })
        if (error) throw error
      }
      // Reusable event join_code path has no join_tokens row to consume.
      if (!tokenData.isEventCode) {
        await supabase.from('join_tokens').update({ used_at: new Date().toISOString(), used_by: user.id }).eq('id', tokenData.id)
      }
      setStatus('success'); setMessage(tokenData.join_type === 'team' ? `Joined ${teamData?.name || 'the team'}!` : `Joined ${eventData?.name || 'the event'}!`)
    } catch (err) { setStatus('error'); setMessage('Join failed: ' + err.message) }
  }

  const goTo = () => { if (tokenData?.join_type === 'team' && tokenData?.team_id) navigate(`/teams/${tokenData.team_id}`); else navigate('/dashboard') }

  return (
    <div style={A.page}>
      <div className="w-full max-w-sm p-4">
        {(status === 'loading' || status === 'validating' || status === 'processing') && (
          <div className="text-center">
            <Loader size={40} className="animate-spin mx-auto mb-4" style={{ color: '#ff7a18' }} />
            <p style={A.muted}>{status === 'processing' ? 'Joining...' : 'Validating link...'}</p>
          </div>
        )}

        {status === 'expired' && (
          <div className="text-center">
            <XCircle size={40} className="mx-auto mb-4" style={{ color: '#ff4136' }} />
            <h2 className="text-xl font-bold mb-2" style={A.display}>LINK EXPIRED</h2>
            <p className="text-sm mb-6" style={A.muted}>This QR code has expired. Ask your teammate to generate a new one.</p>
            <button onClick={() => navigate('/dashboard')} style={A.btnPrimary}>GO TO DASHBOARD</button>
          </div>
        )}

        {status === 'used' && (
          <div className="text-center">
            <XCircle size={40} className="mx-auto mb-4" style={{ color: '#ff4136' }} />
            <h2 className="text-xl font-bold mb-2" style={A.display}>ALREADY USED</h2>
            <p className="text-sm mb-6" style={A.muted}>This QR code has already been scanned.</p>
            <button onClick={() => navigate('/dashboard')} style={A.btnPrimary}>GO TO DASHBOARD</button>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <XCircle size={40} className="mx-auto mb-4" style={{ color: '#ff4136' }} />
            <h2 className="text-xl font-bold mb-2" style={A.display}>INVALID LINK</h2>
            <p className="text-sm mb-6" style={A.muted}>{message || 'This join link is invalid.'}</p>
            <button onClick={() => navigate('/dashboard')} style={A.btnPrimary}>GO TO DASHBOARD</button>
          </div>
        )}

        {status === 'ready' && tokenData && (
          <div style={A.panel}>
            <div className="flex items-center gap-3 mb-6">
              {typeIcon[tokenData.join_type]}
              <div>
                <p className="text-xs" style={{ ...A.faded, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{typeLabel[tokenData.join_type]}</p>
                <p className="font-bold text-lg" style={{ ...A.display, fontSize: '16px' }}>
                  {tokenData.join_type === 'team' ? teamData?.name : eventData?.name}
                </p>
                {eventData?.organizations?.name && <p className="text-sm" style={A.muted}>{eventData.organizations.name}</p>}
              </div>
            </div>
            {tokenData.join_type === 'event_squad' && factionData && (
              <div className="mb-4 p-3" style={{ background: 'rgba(77,214,255,.06)', border: '1px solid rgba(77,214,255,.25)' }}>
                <p className="text-xs mb-1" style={{ color: '#4dd6ff', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Faction</p>
                <p style={{ color: '#aef0ff', fontWeight: 600 }}>{factionData.name}</p>
              </div>
            )}
            <div className="text-sm mb-6 space-y-1" style={A.muted}>
              {tokenData.join_type === 'event' && <p>You'll be added to the event roster as an unassigned operator.</p>}
              {tokenData.join_type === 'event_squad' && <p>You'll be added to the event and placed in the same faction.</p>}
              {tokenData.join_type === 'team' && <p>You'll be added to the team as a member.</p>}
            </div>
            <button onClick={executeJoin} style={A.btnPrimary}>ACCEPT & JOIN</button>
            <button onClick={() => navigate('/dashboard')} style={A.btnGhost}>Decline</button>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <CheckCircle size={40} className="mx-auto mb-4" style={{ color: '#4dd6ff' }} />
            <h2 className="text-xl font-bold mb-2" style={A.display}>JOINED!</h2>
            <p className="text-sm mb-6" style={A.muted}>{message}</p>
            <button onClick={goTo} style={A.btnPrimary}>
              {tokenData?.join_type === 'team' ? 'VIEW TEAM' : 'GO TO DASHBOARD'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
